import { z } from 'zod';

// ---------------------------------------------------------------------------
// BFF wire types (GET /api/labs/tests, POST /api/labs/orders)
// ---------------------------------------------------------------------------

export interface LabTestResponse {
  code: string;
  name: string;
  status?: string;
  display_name?: string | null;
  description?: string | null;
  unit?: string | null;
  price?: number | null;
  group?: string | null;
  loinc_codes?: string[];
  included_test_codes: string[];
  recommended_test_codes: string[];
}

export interface LabTestsResponse {
  provider: string;
  tests: LabTestResponse[];
}

export interface CreateLabOrderRequest {
  patient_identifier: string;
  test_codes: string[];
  comment?: string;
  context_id: string;
}

/** 'unknown': the provider call's outcome is unconfirmed (e.g. it timed out). */
export type LabOrderStatus = 'succeeded' | 'partial_success' | 'failed' | 'unknown';

export interface LabOrderItemResponse {
  test_codes: string[];
  status: 'created' | 'failed' | 'unknown';
}

export interface LabOrderResponse {
  batch_id: string;
  status: LabOrderStatus;
  summary: { requested: number; created: number; failed: number };
  items: LabOrderItemResponse[];
}

// ---------------------------------------------------------------------------
// Client-side models
// ---------------------------------------------------------------------------

export interface LabTest {
  code: string;
  name: string;
  displayName?: string;
  price?: number;
  group?: string;
  includedTestCodes: string[];
  recommendedTestCodes: string[];
}

/** Error codes the order/catalog calls surface to the UI. */
export type LabOrderErrorCode =
  | 'lab_ordering_unavailable'
  | 'laeknanumer_required'
  | 'impersonation_not_allowed'
  | 'invalid_request'
  | 'lab_order_in_progress'
  | 'lab_order_failed';

export interface BloodTestOrderingGate {
  /** Sameind ordering is on for the user (beta + Sameind lab provider + stofnun); everyone else edits the request as text. */
  orderingEnabled: boolean;
  /** The user may actually place an order (læknanúmer on file, not impersonating). */
  canOrder: boolean;
}

/** Patient the sheet already knows, as threaded from the session/todo page. */
export interface BloodTestOrderPatient {
  ssn: string;
  /** transcriber_db patient id, once the patient is registered there. */
  dbId?: number;
  name?: string;
}

/** The todo a Blóðprufubeiðni sheet is opened for. */
export interface BloodTestOrderTask {
  todoId: number;
  /** AI-detected test names from the todo, used to preselect tests. */
  details?: string | null;
  extraData?: Record<string, unknown> | null;
  /**
   * Patient already scoped to the todo/session. When its ssn is a valid
   * 10-digit kennitala, the sheet locks to it (no kennitala input); otherwise
   * it falls back to the kennitala lookup field.
   */
  patient?: BloodTestOrderPatient | null;
  /**
   * Kennitala to prefill in lookup mode, from the session's patient when the todo
   * itself has none. Editable, never locked: the registry name shows before confirm.
   */
  prefillKennitala?: string;
}

export interface BloodTestOrderSheetProps {
  /** The task to order for; the sheet is open while this is set. */
  task: BloodTestOrderTask | null;
  onClose: () => void;
  /** Called after every test in the order was created. */
  onOrdered: () => void;
}

export interface BloodTestOrderFormProps {
  task: BloodTestOrderTask;
  onClose: () => void;
  onOrdered: () => void;
}

export interface UseBloodTestOrderingOptions {
  todoId: number;
  details?: string | null;
  extraData?: Record<string, unknown> | null;
  onOrdered: () => void;
}

export interface BloodTestSelection {
  testCodes: string[];
  unresolvedTerms: string[];
}

export interface BloodTestResolutionNoticeProps {
  unresolvedTerms: string[];
}

export const bloodTestResolutionSchema = z.object({
  provider: z.literal('sameind'),
  source_details: z.string(),
  matches: z.array(z.object({ source_text: z.string(), test_codes: z.array(z.string()).min(1) })),
  unresolved_terms: z.array(z.string()),
});

export interface LockedPatientBlockProps {
  name?: string;
  ssn: string;
}

export interface LookupStatusRowProps {
  isFetching: boolean;
  name: string | null | undefined;
}

export const kennitalaSchema = z.object({
  kennitala: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'Kennitala verður að vera 10 tölustafir.'),
});

export type KennitalaFormValues = z.infer<typeof kennitalaSchema>;

/** The blood-test request as plain text, for users outside the ordering gate. */
export const bloodTestRequestSchema = z.object({ details: z.string() });

export type BloodTestRequestFormValues = z.infer<typeof bloodTestRequestSchema>;
